/**
 * AriaInstall — Tab "Installazione".
 * Mostra lo snippet JS da incollare nel sito e verifica installazione.
 */
import React, { useState } from "react";
import { Copy, Check, Code2, Globe, ExternalLink, ShieldCheck } from "lucide-react";
import { useAriaCustomer } from "./AriaCustomerArea";

export default function AriaInstall() {
    const { activeToken, config } = useAriaCustomer();
    const [copied, setCopied] = useState(false);

    if (!activeToken) return null;

    const backend = "" || "";
    const snippet = `<!-- Aria by TotalKlick -->
<script>
  window.ARIA_TOKEN = "${activeToken}";
</script>
<script src="${backend}/api/widget/aria.js" async defer></script>
<!-- /Aria -->`;

    const copyToClipboard = () => {
        navigator.clipboard.writeText(snippet);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
    };

    return (
        <div className="max-w-3xl" data-testid="tab-install">
            <h1 className="text-white text-[24px] mb-2" style={{ fontWeight: 300, letterSpacing: "-0.02em" }}>Installazione</h1>
            <p className="text-white/65 text-[14px] mb-8" style={{ fontFamily: "Outfit, sans-serif" }}>
                Incolla questo snippet prima del tag <code className="text-[#9EC3FF] px-1 py-0.5 rounded bg-white/[0.06]" style={{ fontFamily: "JetBrains Mono, monospace" }}>&lt;/body&gt;</code> del tuo sito. Aria apparirà entro pochi secondi.
            </p>

            {/* Snippet card */}
            <div className="rounded-2xl overflow-hidden" style={{ background: "rgba(15,18,28,0.7)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
                    <div className="flex items-center gap-2 text-white/65 text-[11.5px]">
                        <Code2 size={12} /> HTML · 5 righe
                    </div>
                    <button type="button" onClick={copyToClipboard}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] text-white text-[12px] transition"
                        data-testid="copy-snippet">
                        {copied ? <><Check size={11} className="text-[#86EFAC]" /> Copiato</> : <><Copy size={11} /> Copia</>}
                    </button>
                </div>
                <pre className="px-4 py-4 text-[12.5px] overflow-x-auto leading-relaxed" style={{ fontFamily: "JetBrains Mono, monospace", color: "#C7E2FF" }}>
{snippet}
                </pre>
            </div>

            {/* Info */}
            <div className="grid sm:grid-cols-2 gap-3 mt-6">
                <InfoCard icon={Globe} title="Dominio configurato" value={config?.site_url || "non impostato"} accent="#9EC3FF" />
                <InfoCard icon={ShieldCheck} title="Token istanza" value={activeToken?.slice(0, 18) + "…"} accent="#86EFAC" mono />
            </div>

            {/* Verifica */}
            <div className="mt-8 p-5 rounded-2xl" style={{ background: "rgba(15,18,28,0.5)", border: "1px solid rgba(255,255,255,0.06)" }}>
                <div className="text-white text-[14px] mb-2" style={{ fontWeight: 500 }}>Come verificare</div>
                <ol className="space-y-2 text-white/70 text-[13px] list-decimal pl-5" style={{ fontFamily: "Outfit, sans-serif" }}>
                    <li>Incolla lo snippet nel tuo sito e pubblica</li>
                    <li>Apri il tuo sito in un browser (cache pulita)</li>
                    <li>Vedrai apparire la bolla Aria in basso a destra entro 2-3 secondi</li>
                    <li>Apri una conversazione di prova per confermare che funzioni</li>
                </ol>
                {config?.site_url && (
                    <a href={config.site_url} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full text-[12.5px] text-white bg-white/[0.06] hover:bg-white/[0.1] transition"
                        data-testid="visit-site">
                        Apri il tuo sito <ExternalLink size={11} />
                    </a>
                )}
            </div>
        </div>
    );
}

function InfoCard({ icon: Icon, title, value, accent, mono }) {
    return (
        <div className="rounded-xl p-4" style={{ background: "rgba(15,18,28,0.5)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="flex items-center gap-2 mb-1.5">
                <Icon size={12} style={{ color: accent }} />
                <span className="text-[10.5px] uppercase tracking-[0.22em] text-white/55" style={{ fontFamily: "JetBrains Mono, monospace" }}>{title}</span>
            </div>
            <div className="text-white text-[13px] truncate" style={mono ? { fontFamily: "JetBrains Mono, monospace" } : {}}>
                {value}
            </div>
        </div>
    );
}
